import { TWO, groupByRank, rankOf, type CardId } from './cards.js';
import type { Move } from './types.js';
import type { PlayerView } from './view.js';

/**
 * Kural tabanlı bot (Seviye 1).
 *
 * ÖNEMLİ: Bot girdi olarak `Match` değil `PlayerView` alır — yani bir insan
 * oyuncunun gördüğünden fazlasını göremez. Bu sayede (a) hile yapması yapısal
 * olarak imkânsız, (b) aynı bot istemcide offline modda da çalışabilir.
 *
 * Politika özeti:
 *   - Bitirebiliyorsan bitir.
 *   - Lider konumunda: en düşük "çöp" bloğunu dök, grubu bölme, 2 ile açma.
 *   - Takipçi konumunda: en ucuz geçerli hamleyi bul; pahalıysa pas geç.
 *     Yumuşak pas olduğu için pas ucuz — sıra tekrar gelecek.
 *   - Rakip bitmek üzereyse ekonomi biter: kes, blokla, harca.
 */

export type Difficulty = 'easy' | 'normal';

type PlayMove = Extract<Move, { type: 'play' }>;

export interface BotTuning {
  /** Rakip bu kadar veya daha az kartla kaldıysa kart biriktirmeyi bırak. */
  rushThreshold: number;
  /** Kendi elimiz bu kadara indiyse artık pas geçip beklemeye değmez. */
  endgameHand: number;
  /** Takipçiyken bu maliyetin üstündeki hamle "israf" sayılır → pas. */
  passThreshold: number;
}

/**
 * Bu değerler tahminle değil, bot-vs-bot ızgara süpürmesiyle seçildi
 * (aynalanmış koltuk dizilimi, 1000 maç). İlk elle konan değerlere karşı
 * %55.8 puan payı. Optimum bir plato — 45-60 arası passThreshold ile
 * 2-4 arası endgameHand istatistiksel olarak ayırt edilemiyor, bu yüzden
 * platonun iç noktası seçildi (aşırı uyum riski en düşük).
 */
export const DEFAULT_TUNING: BotTuning = {
  rushThreshold: 2,
  endgameHand: 3,
  passThreshold: 48,
};

export function chooseMove(
  view: PlayerView,
  difficulty: Difficulty = 'normal',
  tuning: BotTuning = DEFAULT_TUNING,
): Move {
  const moves = view.legalMoves;
  if (moves.length === 0) {
    throw new Error('Bot için geçerli hamle yok — sıra bu koltukta değil');
  }

  const plays = moves.filter((m): m is PlayMove => m.type === 'play');
  if (plays.length === 0) return { type: 'pass' };

  // Bu hamle elini bitiriyorsa her koşulda oyna.
  const finisher = plays.find((p) => p.cards.length === view.hand.length);
  if (finisher) return finisher;

  // Kolay seviye: legalMoves rütbeye göre sıralı geldiği için ilk eleman en düşük.
  if (difficulty === 'easy') return plays[0]!;

  const groups = groupByRank(view.hand);
  const groupSize = (cards: CardId[]): number => groups.get(rankOf(cards[0]!))!.length;

  const opponents = view.handCounts.filter((c, i) => i !== view.seat && c > 0);
  const minOpponent = opponents.length > 0 ? Math.min(...opponents) : Number.MAX_SAFE_INTEGER;

  const leading = view.trick.count === 0;
  if (leading) {
    return plays.reduce((best, p) =>
      leadScore(p.cards, groupSize(p.cards), minOpponent) >
      leadScore(best.cards, groupSize(best.cards), minOpponent)
        ? p
        : best,
    );
  }

  // Rakip bitmek üzere ve elimizde geçerli bir 2 varsa: kes, turu al, dökmeye başla.
  if (minOpponent <= tuning.rushThreshold) {
    const cut = plays.find((p) => rankOf(p.cards[0]!) === TWO);
    if (cut) return cut;
  }

  const best = plays.reduce((a, b) =>
    followCost(a.cards, groupSize(a.cards)) <= followCost(b.cards, groupSize(b.cards)) ? a : b,
  );

  // Baskı yoksa pahalı kartı harcama — yumuşak pas sayesinde sıra bize dönecek.
  const pressured = minOpponent <= tuning.rushThreshold || view.hand.length <= tuning.endgameHand;
  if (!pressured && followCost(best.cards, groupSize(best.cards)) > tuning.passThreshold) {
    return { type: 'pass' };
  }
  return best;
}

/** Yüksek = daha iyi açılış. */
function leadScore(cards: CardId[], groupSize: number, minOpponent: number): number {
  const rank = rankOf(cards[0]!);
  let score = -rank * 3 + cards.length * 4;
  // Üçlüden ikisini koparmak elde ölü tek kart bırakır.
  if (cards.length < groupSize) score -= 12;
  // 2 ile açmak turu kesip liderliği yine bize verir; hiçbir şey kazandırmaz.
  if (rank === TWO) score -= 100;
  // Rakibin tek kartı kaldıysa tekli açmak ona bedava çıkış verir.
  if (minOpponent <= 1 && cards.length === 1) score -= 30;
  return score;
}

/** Düşük = daha ucuz takip hamlesi. */
function followCost(cards: CardId[], groupSize: number): number {
  const rank = rankOf(cards[0]!);
  return (
    rank * 3 +
    (cards.length < groupSize ? 12 : 0) +
    (rank === TWO ? 60 : 0) -
    cards.length * 2
  );
}
